import type { PropsWithChildren, ReactNode } from 'react';
import { Suspense } from 'react';

import type { GrowthBook } from '@growthbook/growthbook-react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createGlobalStyle } from 'styled-components';

import { AppStateProvider } from './AppStateProvider';
import { EmailsQuotaPrefetch } from './components/EmailsQuotaPrefetch';
import { AuthGuard } from './components/guard-components/AuthGuard';
import { QuizNotCompletedGuard } from './components/guard-components/QuizNotCompletedGuard/QuizNotCompletedGuard';
import { RestrictedAccessGuard } from './components/guard-components/RestrictedAccessGuard';
import { UserSettingsGuard } from './components/guard-components/UserSettingsGuard/UserSettingsGuard';
import { ImpersonateBadge } from './components/ImpersonateBadge';
import { ToastProvider } from './components/ToastProvider';
import { AIOutreachPanel } from './pages/company-overview/AIOutreachPanel';
import { Router } from './Router';
import type { AppStore } from './services/appStore';
import { useScrapingMonitoring } from './services/scrapingMonitoring/useScrapingMonitoring';
import type { UseCaseContainer } from './UseCaseProvider';
import { UseCaseProvider } from './UseCaseProvider';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Roboto', sans-serif;
    overflow: hidden;
  }

  * {
    box-sizing: border-box;
  }
`;

export type AppProps = {
  queryClient: QueryClient;
  appStore: AppStore;
  useCases: UseCaseContainer;
  gb: GrowthBook;
};

const AppProviders = ({ children, queryClient, gb, appStore, useCases }: PropsWithChildren<AppProps>) => (
  <QueryClientProvider client={queryClient}>
    <GrowthBookProvider growthbook={gb}>
      <AppStateProvider appStore={appStore}>
        <UseCaseProvider useCases={useCases}>{children}</UseCaseProvider>
      </AppStateProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </GrowthBookProvider>
  </QueryClientProvider>
);

const AppGuards = ({ children }: { children: ReactNode }) => (
  <UserSettingsGuard>
    <AuthGuard>
      <QuizNotCompletedGuard>
        <RestrictedAccessGuard>{children}</RestrictedAccessGuard>
      </QuizNotCompletedGuard>
    </AuthGuard>
  </UserSettingsGuard>
);

export const App = ({ queryClient, appStore, useCases, gb }: AppProps) => {
  useScrapingMonitoring();

  return (
    <>
      <Suspense fallback={<h1>Loading...</h1>}>
        <AppProviders queryClient={queryClient} gb={gb} appStore={appStore} useCases={useCases}>
          <AppGuards>
            <Router />
            <AIOutreachPanel />
            <ToastProvider />
            <ImpersonateBadge />
            <EmailsQuotaPrefetch />
          </AppGuards>
        </AppProviders>
      </Suspense>
      <GlobalStyle />
    </>
  );
};
